import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface MermaidDiagramProps {
  code: string;
  title?: string;
  description?: string;
  className?: string;
  theme?: 'default' | 'dark' | 'forest' | 'neutral';
  fitToContainer?: boolean;
  onRender?: (payload: { svg: SVGSVGElement | null; container: HTMLDivElement | null }) => void;
  surface?: 'elevated' | 'flat';
  padding?: 'none' | 'normal';
}

type MermaidModule = typeof import('mermaid');

let mermaidLoader: Promise<MermaidModule> | null = null;

const loadMermaid = () => {
  if (!mermaidLoader) {
    mermaidLoader = import('mermaid');
  }

  return mermaidLoader;
};

type ThemeVariables = Record<string, string>;

const LIGHT_THEME_VARIABLES: ThemeVariables = {
  background: '#ffffff',
  mainBkg: '#ffffff',
  secondBkg: '#FAEECA',
  primaryColor: '#FF2E5F',
  primaryTextColor: '#ffffff',
  primaryBorderColor: '#42142A',
  secondaryColor: '#FFD452',
  tertiaryColor: '#FAEECA',
  lineColor: '#FFD452',
  border1: '#42142A',
  border2: '#FFD452',
  arrowheadColor: '#FF2E5F',
  fontFamily: 'Montreal, sans-serif',
  fontSize: '14px',
  nodeTextColor: '#32121F',
  clusterBkg: '#FFF6D9',
  clusterBorder: '#E1B856',
  edgeLabelBackground: '#FFF2C6',
  textColor: '#1C1617',
  tertiaryTextColor: '#4C3A3D',
};

const DARK_THEME_VARIABLES: ThemeVariables = {
  background: '#050507',
  mainBkg: '#0E0E12',
  secondBkg: '#161621',
  primaryColor: '#FF2E5F',
  primaryTextColor: '#FFE5EF',
  primaryBorderColor: '#FF4B7B',
  secondaryColor: '#FFD452',
  tertiaryColor: '#262233',
  lineColor: '#FFD452',
  border1: '#FF4B7B',
  border2: '#FFD452',
  arrowheadColor: '#FFD452',
  fontFamily: 'Montreal, sans-serif',
  fontSize: '14px',
  nodeTextColor: '#FFE5EF',
  clusterBkg: '#1C1A26',
  clusterBorder: '#FF4B7B',
  edgeLabelBackground: '#211B2C',
  textColor: '#F4F1FF',
  tertiaryTextColor: '#D6CBFF',
};

type SemanticToken = {
  fill?: string;
  stroke?: string;
  text?: string;
};

const STATUS_STYLES: Record<
  string,
  {
    light: SemanticToken;
    dark: SemanticToken;
  }
> = {
  error: {
    light: { fill: '#FFE0EA', stroke: '#FF2E5F', text: '#681226' },
    dark: { fill: '#431322', stroke: '#FF5C83', text: '#FFC9D7' },
  },
  attention: {
    light: { fill: '#FFF2C6', stroke: '#E5B731', text: '#5C4200' },
    dark: { fill: '#3A2A09', stroke: '#FFD86A', text: '#FFE7A6' },
  },
  correction: {
    light: { fill: '#FFE9D9', stroke: '#ED6A2C', text: '#813208' },
    dark: { fill: '#3B2214', stroke: '#FF8C4D', text: '#FFC9AA' },
  },
  process: {
    light: { fill: '#F5F2FF', stroke: '#5446AB', text: '#352E6B' },
    dark: { fill: '#26203A', stroke: '#7D6BFF', text: '#E4DEFF' },
  },
};

const SHAPE_SELECTOR = 'rect,path,polygon,ellipse,circle';

const BASE_THEME_CSS = `
  .node rect,
  .node polygon,
  .node path {
    rx: 18px;
    ry: 18px;
  }

  .cluster rect {
    rx: 20px;
    ry: 20px;
  }

  .label {
    font-family: "Montreal", sans-serif;
  }

  .edgeLabel {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-family: "Montreal", sans-serif;
  }
`;

const LIGHT_THEME_CSS = `
  .edgeLabel {
    background: rgba(255, 220, 82, 0.18);
    color: #4F4206;
  }

  .label text,
  .label tspan {
    fill: #231820 !important;
  }
`;

const DARK_THEME_CSS = `
  .edgeLabel {
    background: rgba(20, 18, 30, 0.85);
    color: #F9F4FF;
  }

  .label text,
  .label tspan {
    fill: #F4F1FF !important;
  }
`;

const applySemanticStyles = (root: HTMLElement | null, isDark: boolean) => {
  if (!root) return;

  const paletteKey = isDark ? 'dark' : 'light';
  const groups = root.querySelectorAll<SVGGElement>('g');

  groups.forEach((group) => {
    const classNames = Array.from(group.classList ?? []);
    const semanticKey = classNames.find((name) => STATUS_STYLES[name]);

    if (!semanticKey) return;

    const palette = STATUS_STYLES[semanticKey][paletteKey];

    group.querySelectorAll<SVGElement>(SHAPE_SELECTOR).forEach((shape) => {
      if (palette.fill) shape.setAttribute('fill', palette.fill);
      if (palette.stroke) shape.setAttribute('stroke', palette.stroke);
    });

    group.querySelectorAll<SVGTextContentElement>('text').forEach((textNode) => {
      if (palette.text) textNode.setAttribute('fill', palette.text);
    });
  });
};

export const MermaidDiagram: FC<MermaidDiagramProps> = ({
  code,
  title,
  description,
  className,
  theme = 'default',
  fitToContainer = true,
  onRender,
  surface = 'elevated',
  padding = 'normal',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const mermaidId = useMemo(
    () => `mermaid-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const evaluateDarkMode = () => {
      const prefersDark = mediaQuery.matches;
      const classDark = root.classList.contains('dark');
      setIsDarkMode(prefersDark || classDark);
    };

    evaluateDarkMode();

    const observer = new MutationObserver(evaluateDarkMode);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', evaluateDarkMode);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(evaluateDarkMode);
    }

    return () => {
      observer.disconnect();
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', evaluateDarkMode);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(evaluateDarkMode);
      }
    };
  }, []);

  const mermaidConfig = useMemo(
    () => ({
      startOnLoad: false,
      securityLevel: 'loose' as const,
      theme: theme === 'default' ? (isDarkMode ? 'dark' : 'default') : theme,
      themeVariables: isDarkMode ? DARK_THEME_VARIABLES : LIGHT_THEME_VARIABLES,
      flowchart: {
        curve: 'basis',
        padding: 20,
      },
      sequence: {
        useMaxWidth: true,
        diagramMarginX: 18,
        diagramMarginY: 18,
      },
      gantt: {
        barHeight: 18,
        barGap: 6,
      },
      themeCSS: `${BASE_THEME_CSS}${isDarkMode ? DARK_THEME_CSS : LIGHT_THEME_CSS}`,
    }),
    [isDarkMode, theme],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!code) {
      setError('No Mermaid definition provided.');
      return;
    }

    let cancelled = false;

    const renderDiagram = async () => {
      try {
        setError(null);
        const mermaidModule = await loadMermaid();
        const mermaid = mermaidModule.default;

        mermaid.mermaidAPI.reset();
        mermaid.initialize(mermaidConfig);

        const { svg, bindFunctions } = await mermaid.render(mermaidId, code);

        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = svg;

        const svgElement = containerRef.current.querySelector<SVGSVGElement>('svg');
        if (svgElement) {
          svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

          if (fitToContainer) {
            svgElement.removeAttribute('height');
            svgElement.removeAttribute('width');
            svgElement.style.width = '100%';
            svgElement.style.height = '100%';
            svgElement.style.maxWidth = '100%';
            svgElement.style.maxHeight = '100%';
          } else {
            svgElement.style.removeProperty('width');
            svgElement.style.removeProperty('height');
            svgElement.style.removeProperty('max-width');
            svgElement.style.removeProperty('max-height');
          }

          applySemanticStyles(containerRef.current, isDarkMode);
        }

        if (bindFunctions) {
          bindFunctions(containerRef.current);
        }

        if (onRender) {
          onRender({ svg: svgElement ?? null, container: containerRef.current });
        }
      } catch (err) {
        if (cancelled) return;

        console.error('[MermaidDiagram] Failed to render diagram', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to render Mermaid diagram.',
        );

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        if (onRender) {
          onRender({ svg: null, container: containerRef.current });
        }
      }
    };

    renderDiagram();

    return () => {
      cancelled = true;

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      if (onRender) {
        onRender({ svg: null, container: containerRef.current });
      }
    };
  }, [code, mermaidConfig, mermaidId, fitToContainer, isDarkMode, onRender]);

  return (
    <div
      className={cn(
        'mermaid-diagram group relative flex h-full w-full flex-col transition-colors duration-500',
        surface === 'flat'
          ? 'rounded-[28px] border border-transparent bg-transparent shadow-none'
          : 'rounded-3xl border border-black/5 bg-white shadow-sm dark:border-white/10 dark:bg-neutral-950',
        fitToContainer ? 'w-full overflow-hidden' : 'inline-block max-w-none overflow-visible align-top',
        className,
      )}
    >
      <div
        className={
          fitToContainer
            ? cn(
                'relative flex h-full w-full items-center justify-center',
                padding === 'none' ? 'p-0' : 'p-6',
              )
            : cn('relative inline-block', padding === 'none' ? 'p-0' : 'p-6')
        }
      >
        {error ? (
          <div className="flex w-full flex-col gap-2 rounded-lg border border-red-200/70 bg-red-50/60 p-4 text-xs text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
            <p className="font-semibold uppercase tracking-[0.2em]">
              Mermaid render error
            </p>
            <pre className="max-h-64 w-full overflow-auto rounded-md bg-neutral-900/95 p-3 text-[11px] leading-relaxed text-white/90">
              <code>{code}</code>
            </pre>
          </div>
        ) : (
          <div
            aria-live="polite"
            aria-label={title ?? description ?? 'Mermaid diagram'}
            className={cn(
              'mermaid-diagram-container mx-auto min-h-[220px]',
              fitToContainer ? 'w-full' : 'w-auto max-w-none',
            )}
            ref={containerRef}
            role="img"
          />
        )}
      </div>
    </div>
  );
};

export default MermaidDiagram;
